import React from 'react';

/**
 * Skeleton primitives — grey placeholders shaped like the real content, animated with a shimmer
 * (see .mc-skel in index.css). They are purely presentational: no data, no fetching, no state.
 *
 * The point of matching the real layout (row heights, column widths) is that nothing reflows when
 * the data lands — otherwise you just trade one layout jump for two.
 */

export const SkeletonBlock = ({ width = '100%', height = 14, radius = 6, dark = false, style = {} }) => (
  <div className={dark ? 'mc-skel mc-skel-dark' : 'mc-skel'} style={{ width, height, borderRadius: radius, ...style }} />
);

// N stacked lines of text. The last line is shorter, the way a real paragraph ends.
export const SkeletonText = ({ lines = 3, width = '100%', gap = 8, height = 12, dark = false }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap }}>
    {Array.from({ length: lines }, (_, i) => (
      <SkeletonBlock key={i} dark={dark} height={height} width={i === lines - 1 ? '60%' : width} />
    ))}
  </div>
);

// Table body placeholder. `widths` mirrors the real column widths so the header doesn't shift.
export const SkeletonTableRows = ({ rows = 6, widths = ['20%', '30%', '20%', '15%', '15%'], rowHeight = 44 }) => (
  <>
    {Array.from({ length: rows }, (_, r) => (
      <tr key={r}>
        {widths.map((w, c) => (
          <td key={c} style={{ padding: '10px 12px', height: rowHeight, borderBottom: '1px solid #f1f5f9' }}>
            <SkeletonBlock width={w} height={12} />
          </td>
        ))}
      </tr>
    ))}
  </>
);

// Generic card placeholder — a title line plus a few body lines.
export const SkeletonCard = ({ lines = 3, height, dark = false, style = {} }) => (
  <div style={{ background: dark ? '#0f172a' : '#fff', border: `1px solid ${dark ? '#1e293b' : '#e2e8f0'}`, borderRadius: 12, padding: 16, minHeight: height, ...style }}>
    <SkeletonBlock dark={dark} width="45%" height={13} style={{ marginBottom: 14 }} />
    <SkeletonText dark={dark} lines={lines} />
  </div>
);

// Dashboard KPI tile: small label, big number, optional sub-line.
export const SkeletonKpi = ({ withSub = true, dark = false }) => (
  <div style={{ background: dark ? '#0f172a' : '#fff', border: `1px solid ${dark ? '#1e293b' : '#e2e8f0'}`, borderRadius: 12, padding: 16 }}>
    <SkeletonBlock dark={dark} width="55%" height={11} style={{ marginBottom: 12 }} />
    <SkeletonBlock dark={dark} width="40%" height={24} style={{ marginBottom: withSub ? 12 : 0 }} />
    {withSub && <SkeletonBlock dark={dark} width="70%" height={10} />}
  </div>
);

// Whole-page placeholder — the Suspense fallback while a lazy route chunk downloads. Deliberately
// neutral (header + cards, no KPI grid) because it also covers public pages like /tracking.
export const SkeletonPage = () => (
  <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }} aria-busy="true" aria-label="Loading">
    <SkeletonBlock width={220} height={24} style={{ marginBottom: 10 }} />
    <SkeletonBlock width={340} height={12} style={{ marginBottom: 24 }} />
    <SkeletonCard lines={4} style={{ marginBottom: 14 }} />
    <SkeletonCard lines={6} />
  </div>
);

// A list of card-shaped rows — the shape most pages in this app actually use (Returns, RTO
// batches, Pickups, Vendors, Locations, Purchase Orders). `thumb` adds the leading product
// image square; `height` should roughly match the real row so the swap doesn't jump.
export const SkeletonList = ({ rows = 5, thumb = false, height = 64, dark = false, style = {} }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, ...style }} aria-busy="true">
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} style={{
        display: 'flex', alignItems: 'center', gap: 12, minHeight: height, padding: '12px 14px',
        background: dark ? '#0f172a' : '#fff', border: `1px solid ${dark ? '#1e293b' : '#e2e8f0'}`,
        borderRadius: 12,
      }}>
        {thumb && <SkeletonBlock dark={dark} width={40} height={40} radius={8} style={{ flexShrink: 0 }} />}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
          <SkeletonBlock dark={dark} width={`${38 + ((i * 13) % 30)}%`} height={12} />
          <SkeletonBlock dark={dark} width={`${22 + ((i * 17) % 26)}%`} height={10} />
        </div>
        <SkeletonBlock dark={dark} width={72} height={26} radius={8} style={{ flexShrink: 0 }} />
      </div>
    ))}
  </div>
);

// A standalone table placeholder, for the loading branches that replace the whole table rather
// than just its body (SkeletonTableRows covers the tbody-only case).
export const SkeletonTable = ({ rows = 6, cols = 5, dark = false }) => (
  <div style={{
    background: dark ? '#0f172a' : '#fff', border: `1px solid ${dark ? '#1e293b' : '#e2e8f0'}`,
    borderRadius: 12, overflow: 'hidden',
  }} aria-busy="true">
    <div style={{ display: 'flex', gap: 16, padding: '12px 14px', background: dark ? '#111c30' : '#f8fafc', borderBottom: `1px solid ${dark ? '#1e293b' : '#e2e8f0'}` }}>
      {Array.from({ length: cols }, (_, c) => <SkeletonBlock key={c} dark={dark} width={`${100 / cols}%`} height={10} />)}
    </div>
    {Array.from({ length: rows }, (_, r) => (
      <div key={r} style={{ display: 'flex', gap: 16, padding: '13px 14px', borderBottom: `1px solid ${dark ? '#16233a' : '#f1f5f9'}` }}>
        {Array.from({ length: cols }, (_, c) => (
          <SkeletonBlock key={c} dark={dark} width={`${(100 / cols) * (c === 0 ? 0.9 : 0.6 + ((r + c) % 3) * 0.12)}%`} height={12} />
        ))}
      </div>
    ))}
  </div>
);

export default SkeletonBlock;
